import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users,
  CalendarOff,
  CalendarCheck,
  ClipboardList,
  RefreshCw,
  Download,
  AlertTriangle,
  Clock,
  TrendingUp,
  Award,
  FileText,
  GitCompare,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  buildAttendanceCardDetail,
  computeAttendanceDashboardMetrics,
} from "../../lib/attendanceDashboardMetrics";
import {
  buildYearOptionsFromMin,
  clampYearMonthIndex,
  isMonthIndexBeforeMin,
  resolveCompanyMinDate,
} from "../../utils/selectableDateBounds";
import { attendanceService } from "../../services/api";
import {
  DsBadge,
  DsButton,
  DsCard,
  DsIcon,
  DsInput,
  DsLabel,
  DsPageHeader,
} from "../../design-system";
import {
  AnalyticsChartPanel,
  CHART_HEX,
  DashDonut,
  DashHBar,
  DashCombo,
} from "./payroll/payrollDashboardCharts";
import { exportAttendanceDashboardExcel } from "./exportWorkforceDashboardExcel";
import {
  exportAttendanceDashboardPdf,
  exportWorkforceCardPdf,
} from "./exportWorkforceDashboardPdf";
import {
  DashboardCardDetailModal,
  DashboardCompareModal,
  DashboardLoadingOverlay,
  DashboardMetricCard,
  recentPayMonths,
} from "./dashboardSharedUi";

function MiniSpark({ values = [], color = CHART_HEX[0] }) {
  const nums = (values || []).map((v) => Number(v) || 0);
  if (nums.length < 2) return null;
  const max = Math.max(...nums, 1);
  const w = 72;
  const h = 22;
  const step = w / (nums.length - 1);
  const points = nums
    .map((v, i) => `${i * step},${h - (v / max) * (h - 2) - 1}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      <polyline fill="none" stroke={color} strokeWidth="1.75" points={points} />
    </svg>
  );
}

function AlertsPanel({ alerts = [] }) {
  if (!alerts.length) {
    return (
      <div className="rounded-lg border border-[hsl(var(--ds-border))] px-3 py-6 text-center text-xs text-[hsl(var(--ds-muted-fg))]">
        No attendance alerts for this period.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {alerts.map((a) => (
        <div
          key={a.id}
          className={`rounded-lg border px-3 py-2 text-xs ${
            a.tone === "warning"
              ? "border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)]"
              : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]"
          }`}
        >
          <div className="flex items-start gap-2">
            <DsIcon icon={AlertTriangle} size="sm" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-[hsl(var(--ds-fg))]">{a.title}</div>
              <div className="mt-0.5 text-[hsl(var(--ds-muted-fg))]">{a.detail}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RankList({ title, items, emptyLabel, showSub = false }) {
  return (
    <DsCard padding="sm" className="!p-3">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
        {title}
      </h4>
      {!(items || []).length ? (
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((row, idx) => (
            <li
              key={`${row.name}-${idx}`}
              className="flex items-start justify-between gap-2 text-xs"
            >
              <span className="min-w-0 truncate text-[hsl(var(--ds-fg))]">
                {row.name}
                {showSub && row.sub ? (
                  <span className="mt-0.5 block text-[10px] text-[hsl(var(--ds-muted-fg))]">
                    {row.sub}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-[hsl(var(--ds-secondary))]">
                {row.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </DsCard>
  );
}

function ymParts(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) };
}

function toPayMonth(year, month1to12) {
  return `${year}-${String(month1to12).padStart(2, "0")}`;
}

function mapProcessStatusRow(ym, data, metricsOverlay = null) {
  return {
    payMonth: ym,
    label: ym,
    scope: data?.totalCount ?? 0,
    processed: data?.processedCount ?? 0,
    open: data?.unprocessedCount ?? 0,
    locked: data?.stepsFullyLocked ? "Yes" : "No",
    attendanceScope: data?.totalCount ?? 0,
    attendanceProcessed: data?.processedCount ?? 0,
    attendanceUnprocessed: data?.unprocessedCount ?? 0,
    attendanceMonthLocked: !!data?.stepsFullyLocked,
    ...(metricsOverlay || {}),
  };
}

function gridMetricsOverlay(metrics) {
  if (!metrics) return {};
  return {
    presentRate: metrics.presentRate,
    totalAbsentDays: metrics.totalAbsentDays,
    totalPresentDays: metrics.totalPresentDays,
    totalLeaveDays: metrics.totalLeaveDays,
    employeesWithData: metrics.employeesWithData,
    gridLoaded: true,
  };
}

/**
 * Attendance Dashboard — Payroll Dashboard parity (read-only analytics).
 * Data: computeAttendanceDashboardMetrics (loaded grid) + getAttendanceProcessStatus (compare/ops).
 */
export default function AttendanceDashboardPanel({
  darkMode = false,
  isFlipped = false,
  onFlip,
  showFlipButton = true,
  employees = [],
  attendanceData = {},
  leaveTypes = [],
  insightItems = [],
  attendanceDetailData = {},
  selectedMonth = 0,
  selectedYear = new Date().getFullYear(),
  reverseMapping = {},
  loading = false,
  leaveTypeLabels = {},
  onPeriodChange = null,
  canExport = true,
}) {
  const now = new Date();
  const companyMinDate = useMemo(() => resolveCompanyMinDate(), []);
  const [periodMode, setPeriodMode] = useState("month");
  const [month, setMonth] = useState(selectedMonth + 1);
  const [year, setYear] = useState(selectedYear);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [dashTab, setDashTab] = useState("overview");
  const [exporting, setExporting] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [compareMonths, setCompareMonths] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareRows, setCompareRows] = useState([]);
  const [compareFetching, setCompareFetching] = useState(false);
  const [cardDetail, setCardDetail] = useState(null);
  const [currentProcessStatus, setCurrentProcessStatus] = useState(null);
  const [processStatusLoading, setProcessStatusLoading] = useState(false);

  useEffect(() => {
    setMonth(selectedMonth + 1);
    setYear(selectedYear);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (periodMode !== "range") return;
    if (rangeFrom && rangeTo) return;
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    if (!rangeFrom) setRangeFrom(`01-${m}-${y}`);
    if (!rangeTo) setRangeTo(`${lastDay}-${m}-${y}`);
  }, [periodMode, rangeFrom, rangeTo, now]);

  useEffect(() => {
    if (typeof onPeriodChange !== "function") return;
    if (periodMode === "month") {
      onPeriodChange({ periodMode, month, year });
    }
  }, [periodMode, month, year, onPeriodChange]);

  const setPeriodYear = (nextYear) => {
    const period = clampYearMonthIndex(nextYear, month - 1, companyMinDate);
    setYear(period.year);
    setMonth(period.month + 1);
  };
  const setPeriodMonth = (nextMonth1to12) => {
    const period = clampYearMonthIndex(year, nextMonth1to12 - 1, companyMinDate);
    setYear(period.year);
    setMonth(period.month + 1);
  };

  const metrics = useMemo(
    () =>
      computeAttendanceDashboardMetrics({
        employees,
        attendanceData,
        leaveTypes,
        insightItems,
        attendanceDetailData,
        selectedMonth,
        selectedYear,
        reverseMapping,
        periodMode,
        month,
        year,
        rangeFrom,
        rangeTo,
      }),
    [
      employees,
      attendanceData,
      leaveTypes,
      insightItems,
      attendanceDetailData,
      selectedMonth,
      selectedYear,
      reverseMapping,
      periodMode,
      month,
      year,
      rangeFrom,
      rangeTo,
      refreshTick,
    ]
  );

  const currentPayMonth = useMemo(() => {
    if (periodMode !== "month") return null;
    return toPayMonth(year, month);
  }, [periodMode, year, month]);

  const yearOptions = useMemo(
    () =>
      buildYearOptionsFromMin({
        minDate: companyMinDate,
        currentYear: now.getFullYear(),
        yearsBefore: 7,
        yearsAfter: 0,
      }).reverse(),
    [companyMinDate, now]
  );

  const availableForCompare = useMemo(
    () => recentPayMonths(18).map((payMonth) => ({ payMonth })),
    []
  );

  const loadCurrentProcessStatus = useCallback(async () => {
    if (periodMode !== "month") {
      setCurrentProcessStatus(null);
      return;
    }
    setProcessStatusLoading(true);
    try {
      const res = await attendanceService.getAttendanceProcessStatus(month, year);
      setCurrentProcessStatus(res?.data || res || null);
    } catch (err) {
      console.warn("Attendance process status:", err);
      setCurrentProcessStatus(null);
    } finally {
      setProcessStatusLoading(false);
    }
  }, [periodMode, month, year]);

  useEffect(() => {
    loadCurrentProcessStatus();
  }, [loadCurrentProcessStatus, refreshTick]);

  const loadCompareRows = useCallback(async () => {
    if (!compareMonths.length) {
      setCompareRows([]);
      return;
    }
    setCompareFetching(true);
    try {
      const rows = await Promise.all(
        compareMonths.map(async (ym) => {
          const parts = ymParts(ym);
          if (!parts) return null;
          const res = await attendanceService.getAttendanceProcessStatus(
            parts.month,
            parts.year
          );
          const data = res?.data || res;
          const overlay =
            currentPayMonth === ym && periodMode === "month"
              ? gridMetricsOverlay(metrics)
              : null;
          return mapProcessStatusRow(ym, data, overlay);
        })
      );
      const valid = rows.filter(Boolean);
      if (
        currentPayMonth &&
        periodMode === "month" &&
        !compareMonths.includes(currentPayMonth)
      ) {
        valid.unshift({
          payMonth: currentPayMonth,
          label: `${currentPayMonth} (current grid)`,
          scope: currentProcessStatus?.totalCount ?? "—",
          processed: currentProcessStatus?.processedCount ?? "—",
          open: currentProcessStatus?.unprocessedCount ?? "—",
          locked: currentProcessStatus?.stepsFullyLocked ? "Yes" : "No",
          ...gridMetricsOverlay(metrics),
        });
      }
      setCompareRows(valid);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Compare fetch failed"
      );
      setCompareRows([]);
    } finally {
      setCompareFetching(false);
    }
  }, [
    compareMonths,
    currentPayMonth,
    periodMode,
    metrics,
    currentProcessStatus,
  ]);

  useEffect(() => {
    loadCompareRows();
  }, [loadCompareRows]);

  const processRowsForExport = useMemo(() => {
    if (compareRows.length) return compareRows;
    if (!currentProcessStatus || periodMode !== "month") return [];
    return [
      mapProcessStatusRow(
        currentPayMonth,
        currentProcessStatus,
        gridMetricsOverlay(metrics)
      ),
    ];
  }, [compareRows, currentProcessStatus, periodMode, currentPayMonth, metrics]);

  const openCard = useCallback(
    (cardKey) => {
      setCardDetail(buildAttendanceCardDetail(cardKey, { metrics }));
    },
    [metrics]
  );

  const fieldClass =
    "h-8 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-xs text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";

  const showDashboardBody = isFlipped || showFlipButton === false;
  const busy = loading || compareFetching || processStatusLoading;

  const leaveChartLabeled = useMemo(
    () =>
      (metrics.leaveChart || []).map((r) => ({
        label:
          leaveTypeLabels[r.label] ||
          leaveTypeLabels[String(r.label).toUpperCase()] ||
          r.label,
        value: r.value,
      })),
    [metrics.leaveChart, leaveTypeLabels]
  );

  const TAB_ITEMS = [
    { id: "overview", label: "Overview" },
    {
      id: "alerts",
      label: `Alerts${metrics.alerts?.length ? ` (${metrics.alerts.length})` : ""}`,
    },
    { id: "risk", label: "Risk" },
    { id: "leave", label: "Leave mix" },
    { id: "ops", label: "Ops / desk" },
  ];

  const handleExportExcel = async () => {
    if (!canExport) {
      toast.error("You do not have export permission");
      return;
    }
    setExporting(true);
    try {
      const name = await exportAttendanceDashboardExcel({
        metrics,
        periodLabel: metrics.periodLabel,
        compare: compareRows,
        processRows: processRowsForExport,
      });
      toast.success(name ? `Exported ${name}` : "Attendance dashboard exported");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!canExport) {
      toast.error("You do not have export permission");
      return;
    }
    setExporting(true);
    try {
      const name = exportAttendanceDashboardPdf({
        metrics,
        compare: compareRows,
        processRows: processRowsForExport,
        periodLabel: metrics.periodLabel,
      });
      toast.success(name ? `Exported ${name}` : "PDF exported");
    } catch (err) {
      toast.error(err?.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const sparkPresent = [
    Number(metrics.totalPresentDays) || 0,
    Number(metrics.totalLeaveDays) || 0,
    Number(metrics.totalAbsentDays) || 0,
  ];

  const compareChartData = useMemo(() => {
    const rows = compareRows.filter((r) => !String(r.label).includes("(current grid)"));
    if (rows.length < 2) return null;
    return {
      labels: rows.map((r) => r.payMonth || r.label),
      processed: rows.map((r) => Number(r.processed) || 0),
      open: rows.map((r) => Number(r.open) || 0),
    };
  }, [compareRows]);

  return (
    <>
      {showFlipButton && (
        <DsButton type="button" variant="outline" size="sm" onClick={onFlip}>
          <DsIcon icon={RefreshCw} size="sm" />
          {isFlipped ? "Back" : "Flip Dashboard"}
        </DsButton>
      )}

      {showDashboardBody && (
        <div
          className={`att-dashboard-shell relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-[hsl(var(--ds-surface))] ${
            showFlipButton ? "" : "h-full"
          }`}
        >
          <DashboardLoadingOverlay
            show={busy}
            label={compareFetching ? "Loading comparison…" : "Loading analytics…"}
          />

          <div className="ds-page-panel-head">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <DsPageHeader
                title="Attendance Dashboard"
                description={`Period: ${metrics.periodLabel}${busy ? " · Updating…" : ""}`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <DsBadge tone="neutral">
                  {metrics.employeesWithData}/{metrics.headcount} with data
                </DsBadge>
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCompareOpen(true)}
                >
                  <DsIcon icon={GitCompare} size="sm" />
                  Compare
                </DsButton>
                {canExport ? (
                  <>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={exporting || busy}
                      onClick={handleExportExcel}
                    >
                      <DsIcon icon={Download} size="sm" />
                      Excel
                    </DsButton>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={exporting || busy}
                      onClick={handleExportPdf}
                    >
                      <DsIcon icon={FileText} size="sm" />
                      PDF
                    </DsButton>
                  </>
                ) : null}
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setRefreshTick((t) => t + 1);
                    loadCurrentProcessStatus();
                  }}
                >
                  <DsIcon
                    icon={RefreshCw}
                    size="sm"
                    className={busy ? "animate-spin" : ""}
                  />
                  Refresh
                </DsButton>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 lg:px-6">
            <div className="mb-4 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] p-3">
              <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Period
              </div>
              <div className="flex flex-wrap items-end gap-2">
                {[
                  { id: "month", label: "Month" },
                  { id: "year", label: "Year" },
                  { id: "range", label: "Period" },
                ].map((tab) => (
                  <DsButton
                    key={tab.id}
                    type="button"
                    size="sm"
                    variant={periodMode === tab.id ? "secondary" : "ghost"}
                    onClick={() => setPeriodMode(tab.id)}
                  >
                    {tab.label}
                  </DsButton>
                ))}

                {periodMode === "month" && (
                  <>
                    <select
                      className={fieldClass}
                      value={month}
                      onChange={(e) => setPeriodMonth(Number(e.target.value))}
                      aria-label="Month"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option
                          key={i + 1}
                          value={i + 1}
                          disabled={isMonthIndexBeforeMin(year, i, companyMinDate)}
                        >
                          {new Date(2000, i, 1).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                    <select
                      className={fieldClass}
                      value={year}
                      onChange={(e) => setPeriodYear(Number(e.target.value))}
                      aria-label="Year"
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {periodMode === "year" && (
                  <select
                    className={fieldClass}
                    value={year}
                    onChange={(e) => setPeriodYear(Number(e.target.value))}
                    aria-label="Year"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}

                {periodMode === "range" && (
                  <>
                    <div>
                      <DsLabel htmlFor="att-dash-range-from">From (DD-MM-YYYY)</DsLabel>
                      <DsInput
                        id="att-dash-range-from"
                        type="text"
                        className="h-8 max-w-[9rem] text-xs"
                        value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)}
                        placeholder="01-01-2026"
                      />
                    </div>
                    <div>
                      <DsLabel htmlFor="att-dash-range-to">To (DD-MM-YYYY)</DsLabel>
                      <DsInput
                        id="att-dash-range-to"
                        type="text"
                        className="h-8 max-w-[9rem] text-xs"
                        value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)}
                        placeholder="31-03-2026"
                      />
                    </div>
                  </>
                )}
              </div>
              {metrics.dataScopeNote ? (
                <p className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                  {metrics.dataScopeNote}
                </p>
              ) : null}
              {compareMonths.length > 0 ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-primary)/0.08)] px-2 py-1.5 text-[11px]">
                  <span className="font-semibold text-[hsl(var(--ds-fg))]">
                    Comparing: {compareMonths.join(", ")}
                  </span>
                  <span className="text-[hsl(var(--ds-muted-fg))]">
                    Process status from API · pulse metrics from loaded grid when month matches.
                  </span>
                  <DsButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="!h-6 !px-1.5"
                    onClick={() => setCompareMonths([])}
                  >
                    <DsIcon icon={X} size="sm" />
                    Clear
                  </DsButton>
                </div>
              ) : null}
            </div>

            <div className="mb-4 flex flex-wrap gap-1 border-b border-[hsl(var(--ds-border))] pb-2">
              {TAB_ITEMS.map((tab) => (
                <DsButton
                  key={tab.id}
                  type="button"
                  size="sm"
                  variant={dashTab === tab.id ? "secondary" : "ghost"}
                  onClick={() => setDashTab(tab.id)}
                >
                  {tab.label}
                </DsButton>
              ))}
            </div>

            {dashTab === "alerts" ? (
              <AlertsPanel alerts={metrics.alerts || []} />
            ) : dashTab === "risk" ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <RankList
                  title="Highest absent / LOP"
                  items={metrics.highestAbsentEmployees}
                  emptyLabel="No LOP days in this view"
                />
                <RankList
                  title="Top absent departments"
                  items={metrics.topAbsentDepts}
                  emptyLabel="No department LOP"
                />
                <RankList
                  title="Best attendance %"
                  items={metrics.topFullAttendance}
                  emptyLabel="No scores yet"
                  showSub
                />
                <DsCard padding="sm" className="!p-3">
                  <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                    Dept absent days
                  </div>
                  <DashHBar
                    data={metrics.deptAbsentChart || []}
                    valueFormat="count"
                    maxBars={8}
                  />
                </DsCard>
              </div>
            ) : dashTab === "leave" ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <DsCard padding="sm" className="!p-3">
                  <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                    Leave type mix
                  </div>
                  <DashDonut data={leaveChartLabeled} />
                </DsCard>
                <DsCard padding="sm" className="!p-3">
                  <DashHBar data={leaveChartLabeled} valueFormat="count" maxBars={12} />
                </DsCard>
              </div>
            ) : dashTab === "ops" ? (
              <div className="space-y-3">
                {periodMode === "month" && currentProcessStatus ? (
                  <DsCard padding="sm" className="!p-3">
                    <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Process status — {currentPayMonth}
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <DashboardMetricCard
                        label="In scope"
                        value={currentProcessStatus.totalCount ?? 0}
                        icon={Users}
                      />
                      <DashboardMetricCard
                        label="Processed"
                        value={currentProcessStatus.processedCount ?? 0}
                        icon={CalendarCheck}
                        tone="up"
                      />
                      <DashboardMetricCard
                        label="Open"
                        value={currentProcessStatus.unprocessedCount ?? 0}
                        icon={Clock}
                        tone={
                          (currentProcessStatus.unprocessedCount || 0) > 0
                            ? "down"
                            : "default"
                        }
                      />
                      <DashboardMetricCard
                        label="All processed"
                        value={currentProcessStatus.stepsFullyLocked ? "Yes" : "No"}
                        icon={ClipboardList}
                        tone={
                          currentProcessStatus.stepsFullyLocked ? "up" : "default"
                        }
                      />
                    </div>
                    <p className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                      Read-only counts from attendance process status API (no payable-day
                      recalculation).
                    </p>
                  </DsCard>
                ) : periodMode === "month" && processStatusLoading ? (
                  <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                    Loading process status…
                  </p>
                ) : null}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <DashboardMetricCard
                    label="Pending leave"
                    value={metrics.pendingLeaveRequests}
                    icon={ClipboardList}
                    tone={metrics.pendingLeaveRequests > 0 ? "down" : "default"}
                  />
                  <DashboardMetricCard
                    label="Approved leave"
                    value={metrics.approvedLeaveRequests}
                    icon={CalendarCheck}
                    tone="up"
                  />
                  <DashboardMetricCard
                    label="Rejected leave"
                    value={metrics.rejectedLeaveRequests}
                    icon={CalendarOff}
                  />
                  <DashboardMetricCard
                    label="Regularisation signals"
                    value={metrics.regularizationChecks}
                    icon={Clock}
                  />
                </div>
                <RankList
                  title="Top regularisation depts"
                  items={metrics.topRegDepts}
                  emptyLabel="No regularisation signals"
                />
              </div>
            ) : (
              <>
                {(metrics.alerts || []).length > 0 ? (
                  <div className="mb-3">
                    <AlertsPanel alerts={metrics.alerts.slice(0, 3)} />
                    {metrics.alerts.length > 3 ? (
                      <button
                        type="button"
                        className="mt-1 text-[10px] text-[hsl(var(--ds-primary))] underline"
                        onClick={() => setDashTab("alerts")}
                      >
                        View all {metrics.alerts.length} alerts
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className="mb-2 ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Company pulse
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
                  <DashboardMetricCard
                    label="Present days"
                    value={metrics.totalPresentDays}
                    sub={`${metrics.presentRate}% of tracked`}
                    icon={CalendarCheck}
                    tone="up"
                    spark={<MiniSpark values={sparkPresent} color={CHART_HEX[2]} />}
                    onClick={() => openCard("present")}
                  />
                  <DashboardMetricCard
                    label="Absent / LOP"
                    value={metrics.totalAbsentDays}
                    sub={`${metrics.absenteeismRate}% absenteeism`}
                    icon={CalendarOff}
                    tone="down"
                    spark={<MiniSpark values={sparkPresent} color={CHART_HEX[4]} />}
                    onClick={() => openCard("absent")}
                  />
                  <DashboardMetricCard
                    label="Leave days"
                    value={metrics.totalLeaveDays}
                    icon={ClipboardList}
                    onClick={() => openCard("leave")}
                  />
                  <DashboardMetricCard
                    label="Attendance %"
                    value={`${metrics.presentRate}%`}
                    sub={`Avg ${metrics.avgPresentPerEmployee} PR / emp`}
                    icon={TrendingUp}
                    tone="up"
                    onClick={() => openCard("present")}
                  />
                  <DashboardMetricCard
                    label="WO days"
                    value={metrics.totalWoDays}
                    icon={Award}
                    onClick={() => openCard("coverage")}
                  />
                  <DashboardMetricCard
                    label="PH days"
                    value={metrics.totalPhDays}
                    icon={Users}
                    onClick={() => openCard("coverage")}
                  />
                </div>

                {compareRows.length > 0 ? (
                  <div className="mt-4">
                    <div className="mb-2 ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Month comparison (process status)
                    </div>
                    <DsCard padding="sm" className="!p-0 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="ds-table w-full text-xs">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left">Month</th>
                              <th className="px-3 py-2 text-right">Scope</th>
                              <th className="px-3 py-2 text-right">Processed</th>
                              <th className="px-3 py-2 text-right">Open</th>
                              <th className="px-3 py-2 text-right">Locked</th>
                              <th className="px-3 py-2 text-right">Present %</th>
                              <th className="px-3 py-2 text-right">Absent days</th>
                            </tr>
                          </thead>
                          <tbody>
                            {compareRows.map((row) => (
                              <tr
                                key={row.payMonth || row.label}
                                className="border-t border-[hsl(var(--ds-border))]"
                              >
                                <td className="px-3 py-1.5 font-medium">{row.label}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {row.scope ?? row.attendanceScope ?? "—"}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {row.processed ?? row.attendanceProcessed ?? "—"}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {row.open ?? row.attendanceUnprocessed ?? "—"}
                                </td>
                                <td className="px-3 py-1.5 text-right">{row.locked ?? "—"}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {row.presentRate != null ? `${row.presentRate}%` : "—"}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {row.totalAbsentDays ?? "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </DsCard>
                    {compareChartData ? (
                      <DsCard padding="sm" className="!mt-3 !p-3">
                        <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                          Processed vs open by month
                        </div>
                        <DashCombo
                          labels={compareChartData.labels}
                          barValues={compareChartData.processed}
                          lineValues={compareChartData.open}
                          barLabel="Processed"
                          lineLabel="Open"
                        />
                      </DsCard>
                    ) : null}
                  </div>
                ) : null}

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <DsCard padding="sm" className="!p-3">
                    <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Status mix (days)
                    </div>
                    <DashDonut data={metrics.statusMixChart || []} />
                  </DsCard>
                  <DsCard padding="sm" className="!p-3">
                    <AnalyticsChartPanel
                      title="Present vs absent depts"
                      data={{}}
                      labels={(metrics.deptPresentChart || []).slice(0, 6).map((d) => d.label)}
                      values={(metrics.deptPresentChart || []).slice(0, 6).map((d) => d.value)}
                      lineValues={(metrics.deptAbsentChart || [])
                        .slice(0, 6)
                        .map((d) => d.value)}
                      defaultMode="bar"
                    />
                  </DsCard>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <RankList
                    title="Top present departments"
                    items={metrics.topPresentDepts}
                    emptyLabel="No present data"
                  />
                  <RankList
                    title="Highest LOP employees"
                    items={metrics.highestAbsentEmployees}
                    emptyLabel="No LOP in this view"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <DashboardCompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        availableMonths={availableForCompare}
        selected={compareMonths}
        aspects={[]}
        selectedAspects={[]}
        title="Compare attendance months"
        onApply={({ months }) => setCompareMonths(months || [])}
      />
      <DashboardCardDetailModal
        open={!!cardDetail}
        detail={cardDetail}
        onClose={() => setCardDetail(null)}
        canExport={canExport}
        onExportPdf={exportWorkforceCardPdf}
      />
    </>
  );
}
